import {
	Alert,
	Card,
	NumberInput,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useSnapshot } from "valtio";
import { assistantUsageTexts } from "@/locales/assistant-usage";
import { i18nStore } from "@/store/i18n";
import type { AssistantSettingsDto } from "@/types/ai-assistant-admin";
import { CAP_SETTINGS_LIMITS } from "@/types/ai-assistant-usage";
import {
	CAP_LIMIT_RULES,
	type CapNumberKey,
	type CapSettingsKey,
} from "./cap-settings.logic";
import { BELOW_INPUT_ORDER } from "./limit-groups";

interface Props {
	form: AssistantSettingsDto;
	errors: Partial<Record<string, string>>;
	disabled: boolean;
	onChange: <K extends keyof AssistantSettingsDto>(
		key: K,
		value: AssistantSettingsDto[K],
	) => void;
}

const USD_STEP_DECIMALS = 2;
const FIELD_ORDER: CapNumberKey[] = [
	"chatTokenLimitMonthly",
	"voiceMinutesLimitDaily",
	"voiceMinutesLimitMonthly",
	"chatUsdLimitDaily",
	"chatUsdLimitMonthly",
	"voiceUsdLimitDaily",
	"voiceUsdLimitMonthly",
];

/** Batas harian/bulanan plafon AI (mode pantau: tersimpan & dipakai meter, belum memblokir). */
export function CapSettingsSection({
	form,
	errors,
	disabled,
	onChange,
}: Props) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const t = assistantUsageTexts[uiLang === "en" ? "en" : "id"];
	const labels: Record<CapNumberKey, string> = {
		chatTokenLimitMonthly: t.chatTokenMonthly,
		voiceMinutesLimitDaily: t.voiceMinDaily,
		voiceMinutesLimitMonthly: t.voiceMinMonthly,
		chatUsdLimitDaily: t.chatUsdDaily,
		chatUsdLimitMonthly: t.chatUsdMonthly,
		voiceUsdLimitDaily: t.voiceUsdDaily,
		voiceUsdLimitMonthly: t.voiceUsdMonthly,
	};

	const numberField = (key: CapNumberKey) => {
		const rule = CAP_LIMIT_RULES[key];
		const isUsd = rule.unit === "usd";
		return (
			<NumberInput
				key={key}
				label={labels[key]}
				description={t.zeroUnlimited}
				inputWrapperOrder={[...BELOW_INPUT_ORDER]}
				min={0}
				max={rule.max}
				allowDecimal={isUsd}
				decimalScale={isUsd ? USD_STEP_DECIMALS : 0}
				thousandSeparator="."
				decimalSeparator=","
				value={form[key]}
				onChange={(v) => onChange(key, typeof v === "number" ? v : Number.NaN)}
				error={errors[key]}
				disabled={disabled}
			/>
		);
	};
	const warnKey: CapSettingsKey = "capWarnPercent";

	return (
		<Card withBorder radius="md" p="lg" data-cap-settings>
			<Stack gap="sm">
				<Title order={4}>{t.settingsTitle}</Title>
				<Text size="sm" c="dimmed">
					{t.settingsIntro}
				</Text>
				<Alert color="blue" icon={<IconInfoCircle size={16} />} p="xs">
					<Text size="xs">{t.modeNote}</Text>
				</Alert>
				<SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
					{FIELD_ORDER.map(numberField)}
					<NumberInput
						label={t.warnPercentLabel}
						description={t.warnPercentHint}
						inputWrapperOrder={[...BELOW_INPUT_ORDER]}
						min={CAP_SETTINGS_LIMITS.warnPercentMin}
						max={CAP_SETTINGS_LIMITS.warnPercentMax}
						allowDecimal={false}
						value={form[warnKey]}
						onChange={(v) =>
							onChange(warnKey, typeof v === "number" ? v : Number.NaN)
						}
						error={errors[warnKey]}
						disabled={disabled}
					/>
				</SimpleGrid>
			</Stack>
		</Card>
	);
}
